import { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await api.get('/api/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Fetch user error:', error.response?.status, error.message);
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                setToken(null);
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password, restaurantId) => {
        const payload = { email, password };
        if (restaurantId) payload.restaurantId = restaurantId;
        const response = await api.post('/api/auth/login', payload);
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            setToken(response.data.token);
            setUser(response.data.user);
        }
        return response.data;
    };

    const googleLogin = async (idToken, returnOrigin, restaurantId, userId) => {
        const urlParams = new URLSearchParams(window.location.search);
        const origin = returnOrigin || urlParams.get('return_origin');
        const response = await api.post('/api/auth/google', { idToken, returnOrigin: origin, restaurantId, userId });
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            setToken(response.data.token);
            setUser(response.data.user);
        }
        return response.data;
    };

    const register = async (userData) => {
        const response = await api.post('/api/auth/register', userData);
        // Note: Register now requires OTP verification, so we don't set token here anymore
        return response.data;
    };

    const setAuth = (newToken, newUser) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(newUser);
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('active_branch_slug');
        setToken(null);
        setUser(null);
    };

    const hasFeatureAccess = (requiredFeature) => {
        // Bypass plan restrictions on localhost for development/testing convenience
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return true;
        }

        // If no user is logged in (e.g. guest customer browsing menu), resolve plan context using storefront tenant settings.
        let plan = 'Starter';
        let isTrialActive = false;

        if (user) {
            if (user.role === 'super_admin') return true;

            // Check if user's free trial is active (subscription_status === 'trialing' AND trial_end in the future)
            isTrialActive = user.subscription_status === 'trialing' && 
                            user.trial_end && 
                            new Date(user.trial_end) > new Date();
            plan = user.plan_name || 'Starter';
        } else if (window.__tenantInfo) {
            isTrialActive = window.__tenantInfo.subscription_status === 'trialing' && 
                            window.__tenantInfo.trial_end && 
                            new Date(window.__tenantInfo.trial_end) > new Date();
            plan = window.__tenantInfo.plan_name || 'Starter';
        }

        if (isTrialActive) {
            return true;
        }

        // 1. Premium plan has access to everything
        if (plan === 'Premium') return true;

        // 2. Starter plan has NO access to any app downloads
        if (plan === 'Starter') {
            if (['customer_app', 'admin_app', 'kitchen_app', 'delivery_app'].includes(requiredFeature)) {
                return false;
            }
        }

        // 3. Professional plan has access to Customer and Admin apps only
        if (plan === 'Professional') {
            if (['customer_app', 'admin_app'].includes(requiredFeature)) return true;
            if (['kitchen_app', 'delivery_app', 'delivery_dashboard'].includes(requiredFeature)) return false;
            if (requiredFeature === 'kitchen_dashboard') return true;
        }

        // 4. Feature checks
        if (requiredFeature === 'kitchen_dashboard') {
            return plan === 'Professional';
        }

        if (requiredFeature === 'delivery_dashboard') {
            return false; // only Premium can access delivery dashboard (since Premium returns true above)
        }

        return true; // other basic features are allowed
    };

    return (
        <AuthContext.Provider value={{ user, token, login, googleLogin, register, logout, setAuth, loading, hasFeatureAccess }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    return useContext(AuthContext);
}